import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "src/context";
import {
  Button,
  ChevronDownIcon,
  FormTableBody,
  FormTableHeader,
  User02Icon,
} from "@porchpass/ui";
import { CollapsibleContent } from "src/components/CollapsibleContent";
import { DlaFormTableShell } from "src/components/DlaFormTableShell";
import { IconActionButton } from "src/components/IconActionButton";
import ProgressListItem from "./ProgressListItem";
import { progress_list_labels } from "src/constants";
import type {
  DtoBorrowerProgress,
  DtoCoBorrowerProgress,
} from "@porchpass/api-client";
type ProgressListLabelShape<
  TBorrowerType extends DtoBorrowerProgress | DtoCoBorrowerProgress,
> = {
  key: keyof TBorrowerType;
  titleText: string;
  url: string;
};
type ProgressListContainerProps<
  TBorrowerType extends DtoBorrowerProgress | DtoCoBorrowerProgress,
> = {
  borrower: Partial<TBorrowerType>;
  isCoborrower?: boolean;
  applicationId: string;
  applicationStatus?: string;
  isWaitingOnEsignatureService?: boolean;
  editSectionClickHandler: (openLink: () => void) => void;
  removeCoborrowerClickHandler?: () => void;
  isAppSignAndSubmitReady: boolean;
};

export default <
  TBorrowerType extends DtoBorrowerProgress | DtoCoBorrowerProgress,
>({
  borrower,
  isCoborrower = false,
  applicationId,
  applicationStatus,
  isWaitingOnEsignatureService,
  editSectionClickHandler,
  removeCoborrowerClickHandler,
  isAppSignAndSubmitReady,
}: ProgressListContainerProps<TBorrowerType>) => {
  const { setUserData } = useContext(UserContext);
  const navigate = useNavigate();
  const showSubmit =
    isAppSignAndSubmitReady && borrower.status === "READY_TO_SIGN_SUBMIT";
  const shouldContainerBeOpen = !(isWaitingOnEsignatureService || showSubmit);
  const [isContainerOpen, setIsContainerOpen] = useState(shouldContainerBeOpen);

  useEffect(() => {
    setIsContainerOpen(shouldContainerBeOpen);
  }, [shouldContainerBeOpen]);

  const isDisabled =
    borrower.status === "INVITED" ||
    isWaitingOnEsignatureService ||
    applicationStatus === "REVIEWED";

  const borrowerDisplay =
    borrower.first_name && borrower.last_name
      ? borrower.first_name + " " + borrower.last_name
      : "Waiting for Co-Borrower";
  const statusText =
    borrower.status === "WAITING_ON_ESIGNATURE_SERVICE"
      ? "Saving Changes"
      : borrower.status === "READY_TO_SIGN_SUBMIT"
        ? "Ready to Sign & Submit"
        : borrower.status?.replaceAll("_", " ").toLowerCase();

  const openLink = () => {
    setUserData?.((prev) => ({
      ...prev,
      borrower_id: borrower.borrower_id,
    }));
    navigate("../signature");
  };

  const showRemoveCoborrower = isCoborrower && applicationStatus !== "REVIEWED";
  const showSavingButton = borrower.status === "WAITING_ON_ESIGNATURE_SERVICE";
  const hasFooterWhenCollapsed =
    showRemoveCoborrower || showSubmit || showSavingButton;
  const shellClassName =
    !isContainerOpen && !hasFooterWhenCollapsed
      ? "!pb-0 [&>div]:!space-y-0"
      : undefined;

  return (
    <DlaFormTableShell className={shellClassName}>
      <FormTableHeader>
        <div className="flex flex-col gap-3">
          <div className="text-base font-medium capitalize leading-none text-[#111827]">
            {borrowerDisplay}
          </div>
          <div
            className="text-xs capitalize leading-none text-[#4075ff]"
            data-replay-safe
          >
            {statusText}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <IconActionButton
            type="button"
            onClick={() => setIsContainerOpen(!isContainerOpen)}
            tabIndex={0}
            aria-label="Expand/Collapse Toggle"
            aria-expanded={isContainerOpen}
          >
            <ChevronDownIcon
              variant="stroke-sharp"
              size={14}
              className={`transition-transform duration-300 ease-out ${
                isContainerOpen ? "rotate-180" : ""
              }`}
            />
          </IconActionButton>
        </div>
      </FormTableHeader>
      <CollapsibleContent
        isOpen={isContainerOpen}
        className={!isContainerOpen ? "!m-0" : undefined}
      >
        <FormTableBody className="!p-3">
          {borrower &&
            (progress_list_labels as ProgressListLabelShape<TBorrowerType>[])
              .filter(({ key }) => key in borrower)
              .map(({ key, titleText, url }) => (
                <ProgressListItem
                  key={key as string}
                  borrowerId={borrower.borrower_id ?? ""}
                  applicationId={applicationId}
                  applicationStatus={applicationStatus}
                  text={titleText}
                  status={!(borrower[key] === "INCOMPLETE")}
                  url={url}
                  isDisabled={isDisabled}
                  editSectionClickHandler={editSectionClickHandler}
                />
              ))}
        </FormTableBody>
      </CollapsibleContent>
      {showRemoveCoborrower && (
        <div className="box-border w-full p-2">
          <Button
            type="button"
            variant="destructive-ghost"
            className="w-full justify-center rounded-2xl p-4 text-base"
            onClick={removeCoborrowerClickHandler}
          >
            <User02Icon size={22} className="-m-[3px]" />
            Remove Co-borrower
          </Button>
        </div>
      )}
      {showSubmit && (
        <div className="box-border w-full p-2">
          <Button
            type="button"
            variant="primary"
            size="l"
            className="w-full max-w-full"
            onClick={openLink}
          >
            Sign & Submit
          </Button>
        </div>
      )}
      {showSavingButton && (
        <div className="box-border w-full p-2">
          <Button
            type="button"
            variant="primary"
            size="l"
            className="w-full max-w-full"
            disabled
            loading
          >
            Saving changes
          </Button>
        </div>
      )}
    </DlaFormTableShell>
  );
};
